// Bildzeichen der versicherten Gefahren (Immobilienvertrag).
//
// WOFÜR: In der Immobilienmappe steht je Vertrag eine Tabelle der versicherten
// Gefahren. Zwölf Zeilen Text wie „Fahrzeuganprall, Rauch und
// Überschalldruckwellen (FR)" liest niemand quer — das Zeichen sagt auf einen
// Blick, ob Feuer, Wasser oder Sturm gedeckt ist. Genau darum geht es beim
// Blick in die Mappe.
//
// Wie `brancheIcons.tsx` rohe Fragmente im 36×36-Raster (kein fertiges <svg>),
// damit dieselben Pfade im HTML-Fluss UND innerhalb eines SVG sitzen können.
// Die Schlüssel sind die `id`s aus `immobilieContractStep/gefahrenConfig.ts`.

import { BRANCHE_FALLBACK_ICON } from "./brancheIcons";
import { GEFAHREN_ROWS } from "../components/immobilieContractStep/gefahrenConfig";

export { ICON_VIEWBOX, IconRahmen } from "./brancheIcons";

/** Gefahr-ID (`GEFAHREN_ROWS[].id` bzw. `subRows[].id`) → Zeichen. */
export const GEFAHREN_ICONS: Record<string, JSX.Element> = {
  feuer: (
    <>
      <path d="M18 4c1 5-3 7-3 11a3 3 0 0 0 6 0c0-1.5-.5-2.5-.5-2.5C24 15 27 18.5 27 23a9 9 0 0 1-18 0c0-6 6-9 9-19z" />
    </>
  ),
  leitungswasser: (
    <>
      <path d="M18 5c5 7 8 11 8 15a8 8 0 0 1-16 0c0-4 3-8 8-15z" />
      <path d="M14 21c0 3 1.8 4.8 4 5" />
    </>
  ),
  sturm_hagel: (
    <>
      <path d="M9 15a6 6 0 0 1 11.5-2.3A5 5 0 0 1 28 17a4.5 4.5 0 0 1-4.5 4.5H10A5 5 0 0 1 9 15z" />
      <path d="M13 26.5v1M18 27.5v1M23 26.5v1M15.5 30.5v1M20.5 30.5v1" />
    </>
  ),
  we_elementar: (
    <>
      <path d="M4 24c3.5-2.5 6.5-2.5 10 0s6.5 2.5 10 0 6.5-2.5 8-1.5" />
      <path d="M4 30c3.5-2.5 6.5-2.5 10 0s6.5 2.5 10 0 6.5-2.5 8-1.5" />
      <path d="M8 18l6-9 5 6 4-4 5 7" />
    </>
  ),
  ibs: (
    <>
      <path d="M14 4l-3 12h6l-2 8 11-13h-7l3-7z" />
      <path d="M6 30h24" />
    </>
  ),
  fr: (
    <>
      <path d="M4 22h13l4-5h5l4 5v5H4z" />
      <circle cx="10" cy="29" r="2.5" />
      <circle cx="24" cy="29" r="2.5" />
      <path d="M4 10l4 4M10 6v5M17 8l-3 4" />
    </>
  ),
  ug: (
    <>
      <circle cx="18" cy="18" r="13" />
      <path d="M14 14a4 4 0 1 1 5 4v3" />
      <path d="M18 26v1" />
    </>
  ),
  wug: (
    <>
      <circle cx="14" cy="18" r="10" />
      <path d="M11 15a3 3 0 1 1 3.6 2.9V20" />
      <path d="M14 24v1" />
      <path d="M26 9v18M30 12v12" />
    </>
  ),
  glas_allgemein: (
    <>
      <rect x="7" y="6" width="22" height="24" rx="1.5" />
      <path d="M18 6l-4 10 6 3-5 11" />
    </>
  ),
  glas_gesamt_ohne_schaufenster: (
    <>
      <rect x="6" y="7" width="10" height="22" rx="1.5" />
      <rect x="20" y="7" width="10" height="22" rx="1.5" />
      <path d="M11 7l-2 9 4 3-3 10" />
    </>
  ),
  glas_gesamt_inkl_schaufenster: (
    <>
      <rect x="4" y="9" width="28" height="18" rx="1.5" />
      <path d="M4 27h28" />
      <path d="M13 9l-3 8 5 3-4 7" />
      <path d="M22 9v18" />
    </>
  ),
  tv: (
    <>
      <circle cx="18" cy="18" r="5" />
      <path d="M18 4v4M18 28v4M4 18h4M28 18h4M8.2 8.2l2.8 2.8M25 25l2.8 2.8M27.8 8.2L25 11M11 25l-2.8 2.8" />
    </>
  ),
};

/**
 * Unterzeile → Hauptzeile (`we_a` → `we_elementar`, `ibs_b` → `ibs`). Aus dem
 * Katalog abgeleitet statt über ein Namensmuster geraten: die Unterzeilen-IDs
 * folgen keinem, und ein neuer Untereintrag soll nicht still beim
 * Auffangzeichen landen.
 */
const HAUPTZEILE: Record<string, string> = Object.fromEntries(
  GEFAHREN_ROWS.flatMap((r) => (r.subRows ?? []).map((s) => [s.id, r.id])),
);

/** Zeichen einer Gefahr; unbekannte ID → Auffangzeichen (wie bei den Branchen). */
export const gefahrIcon = (id: string | null | undefined): JSX.Element => {
  const key = (id ?? "").trim();
  if (!key) return BRANCHE_FALLBACK_ICON;
  const eltern = HAUPTZEILE[key];
  return (
    GEFAHREN_ICONS[key] ??
    (eltern ? GEFAHREN_ICONS[eltern] : undefined) ??
    BRANCHE_FALLBACK_ICON
  );
};
